import { Component } from '@theme/component';
import { parseScheduleInstant } from '@theme/utilities';

/**
 * Announcement bar: picks the visible slide from schedule (start/end dates).
 * Multiple blocks are filtered by date; there is no manual or timed slide rotation.
 * When several slides are eligible, a time-bounded (scheduled) one is preferred over
 * an “always” slide so a dated promo can override a permanent bar without reordering blocks.
 * If every scheduled slide is outside its window and there is no “always” slide, no slide
 * is selected (no fallback to “show all”, which used to surface expired messages).
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} [slides]
 *
 * @extends {Component<Refs>}
 */
export class AnnouncementBar extends Component {
  /** @type {ReturnType<typeof setInterval> | undefined} */
  #countdownInterval = undefined;
  /** @type {ResizeObserver | undefined} */
  #overflowObserver = undefined;
  /** @type {MediaQueryList | undefined} */
  #overflowMediaQuery = undefined;
  /** @type {(() => void) | undefined} */
  #overflowMediaHandler = undefined;

  connectedCallback() {
    super.connectedCallback();
    this.#applyScheduling();
    this.#observeOverflow();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    clearInterval(this.#countdownInterval);
    this.#countdownInterval = undefined;
    this.#overflowObserver?.disconnect();
    this.#overflowObserver = undefined;
    if (this.#overflowMediaQuery && this.#overflowMediaHandler) {
      this.#overflowMediaQuery.removeEventListener('change', this.#overflowMediaHandler);
    }
    this.#overflowMediaQuery = undefined;
    this.#overflowMediaHandler = undefined;
  }

  /** @param {number} _index Unused; setter exists so `this.current = 0` can refresh visibility. */
  set current(_index) {
    const activeSlides = this.#activeSlides();

    if (activeSlides.length === 0) {
      this.refs.slides?.forEach((slide) => {
        slide.setAttribute('aria-hidden', 'true');
        slide.toggleAttribute('hidden', slide.dataset.scheduledHidden === 'true');
      });
      return;
    }

    const active = activeSlides[0];

    this.refs.slides?.forEach((slide) => {
      const isActive = slide === active;
      slide.toggleAttribute('hidden', !isActive && slide.dataset.scheduledHidden === 'true');
      slide.setAttribute('aria-hidden', `${!isActive}`);
    });
  }

  #activeSlides() {
    const slides = this.refs.slides ?? [];
    const now = Date.now();

    const eligible = slides.filter((slide) => {
      const startAt = slide.dataset.startAt;
      const endAt = slide.dataset.endAt;

      const start = startAt ? parseScheduleInstant(startAt, 'start') : undefined;
      const end = endAt ? parseScheduleInstant(endAt, 'end') : undefined;

      if (startAt && Number.isNaN(start)) return true;
      if (endAt && Number.isNaN(end)) return true;

      if (typeof start === 'number' && now < start) return false;
      if (typeof end === 'number' && now > end) return false;

      return true;
    });

    // Do not fall back to showing every slide when all time-bounded promos are outside
    // their window — that made an expired first message “win” over nothing.
    if (eligible.length === 0) return [];

    eligible.sort((a, b) => {
      const aBounded = Boolean(a.dataset.startAt || a.dataset.endAt);
      const bBounded = Boolean(b.dataset.startAt || b.dataset.endAt);
      if (aBounded !== bBounded) return aBounded ? -1 : 1;
      return 0;
    });

    return eligible;
  }

  #applyScheduling() {
    const slides = this.refs.slides ?? [];

    slides.forEach((slide) => {
      const hasSchedule = Boolean(slide.dataset.startAt) || Boolean(slide.dataset.endAt);
      slide.dataset.scheduledHidden = hasSchedule ? 'true' : 'false';
    });

    const reevaluate = () => {
      const activeSlides = this.#activeSlides();
      const activeSet = new Set(activeSlides);

      slides.forEach((slide) => {
        const shouldHide = slide.dataset.scheduledHidden === 'true' && !activeSet.has(slide);
        slide.toggleAttribute('hidden', shouldHide);
      });

      this.current = 0;
    };

    reevaluate();
    this.#countdownInterval = setInterval(() => {
      reevaluate();
      this.#updateCountdownLabels();
    }, 1000);

    this.#updateCountdownLabels();
  }

  /**
   * On mobile, enable a marquee animation only when the row's natural single-line
   * width exceeds the container. CSS forces the group to be a single line on mobile
   * so we can compare scrollWidth (natural width) directly against clientWidth.
   *
   * Re-measures on resize, breakpoint change, font load, and window load — all of
   * which can shift content widths after the initial connectedCallback.
   */
  #observeOverflow() {
    if (typeof ResizeObserver === 'undefined') return;

    const rows = Array.from(this.querySelectorAll('.announcement-bar__row'));
    if (rows.length === 0) return;

    const mq = window.matchMedia('(max-width: 749px)');
    this.#overflowMediaQuery = mq;

    /** @param {Element} row */
    const update = (row) => {
      if (!(row instanceof HTMLElement)) return;

      if (!mq.matches) {
        row.classList.remove('announcement-bar__row--marquee');
        return;
      }

      const group = row.querySelector('.announcement-bar__group:not(.announcement-bar__group--clone)');
      if (!(group instanceof HTMLElement)) return;

      row.classList.remove('announcement-bar__row--marquee');

      if (group.scrollWidth > row.clientWidth + 1) {
        row.classList.add('announcement-bar__row--marquee');
      }
    };

    const updateAll = () => rows.forEach(update);

    this.#overflowObserver = new ResizeObserver((entries) => {
      entries.forEach((entry) => update(/** @type {HTMLElement} */ (entry.target)));
    });

    rows.forEach((row) => {
      this.#overflowObserver?.observe(row);
      update(row);
    });

    this.#overflowMediaHandler = updateAll;
    mq.addEventListener('change', this.#overflowMediaHandler);

    if ('fonts' in document) {
      document.fonts.ready.then(updateAll).catch(() => {});
    }
    if (document.readyState !== 'complete') {
      window.addEventListener('load', updateAll, { once: true });
    }
  }

  #updateCountdownLabels() {
    const nodes = this.querySelectorAll('[data-countdown-to]');
    const now = Date.now();

    nodes.forEach((node) => {
      const target = parseScheduleInstant(node.getAttribute('data-countdown-to') ?? '', 'end');
      if (Number.isNaN(target)) return;

      const remaining = Math.max(0, target - now);
      const totalSeconds = Math.floor(remaining / 1000);
      const days = Math.floor(totalSeconds / 86400);
      const hours = Math.floor((totalSeconds % 86400) / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;

      // IMPORTANT: keep this format in sync with the phantom span rendered
      // by `blocks/announcement.liquid`. The phantom pins intrinsic width.
      const value = `${String(days).padStart(2, '0')} d : ${String(hours).padStart(2, '0')} h : ${String(minutes).padStart(2, '0')} m : ${String(seconds).padStart(2, '0')} s`;

      const text = node.querySelector('.announcement-bar__countdown-value-text');
      if (text && text.textContent !== value) {
        text.textContent = value;
      }
    });
  }
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}
